import React from "react";
import {
  Button,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
} from "semantic-ui-react";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

// Serializer
import LeaveTypeSerializer from "issara-sdk/types/LeaveTypeSerializer_apps_HRM";

// Table Rendering
import {
  LeaveRequestListType,
  RenderLeaveType,
  RenderStatus,
  RenderDocument,
  RenderDisplayShiftTime,
  RenderDisplayLeaveTime,
  RenderDisplayRequestedHour,
  RenderDisplayQuotaLeaveHour,
  formatLeaveRequestData,
} from "./LeaveRequestListRendering";

// Style
import "./TableLeaveRequestList.scss";

// Interface
import { PickedProps, SetProp } from "./sequence/SelfServiceSchedule";
import { ShiftTimeMapType } from "./CardSelfServiceLeaveHistory";
import moment from "moment";

// Type
type TableSelfServiceLeaveHistoryProps = {
  // seq
  setProp: SetProp;
  // func
  onOpenRequest: (id: number) => void;
  onCancel: (id: number) => void;
  // data
  leaveTypeList: LeaveTypeSerializer[];
  leaveRequestList: LeaveRequestListType[];
  shiftTimeMap: ShiftTimeMapType;
  // options
  paramKey: string;
} & PickedProps;

type RenderActionButtonProps = {
  // seq
  setProp: SetProp;
  // func
  onOpenRequest: (id: number) => void;
  onCancel: (id: number) => void;
  // data
  item: LeaveRequestListType;
  paramKey: string;
} & PickedProps;

/**================================================================================================
 **                                         RENDERING
 *================================================================================================**/

const RenderActionButton = (props: RenderActionButtonProps) => {
  const { item, paramKey, onOpenRequest, onCancel } = props;

  if (["X", "A"].includes(item.status)) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <Button
        id={`TableSelfServiceLeaveHistory-Edit-${item.id}`}
        color="orange"
        size="small"
        onClick={() => onOpenRequest(item.id)}
      >
        Edit
      </Button>
      <ButtonLoadCheck
        id={`TableSelfServiceLeaveHistory-Cancel-${item.id}`}
        // func
        setProp={props.setProp}
        onClick={() => onCancel(item.id)}
        // data
        paramKey={`${paramKey}_CANCEL_${item.id}`}
        buttonLoadCheck={props.buttonLoadCheck?.[`${paramKey}_CANCEL_${item.id}`]}
        // config
        title="Cancel"
        color="red"
        name="CANCEL"
        size="small"
      />
    </div>
  );
};

/**================================================================================================
 **                                         HANDLER
 *================================================================================================**/

const getLeaveDates = (item: LeaveRequestListType) => {
  const workShift =
    item.status === "P"
      ? item.work_shift_detail?.work_shift_list || []
      : item.work_shift_detail?.work_shift || [];

  const rawDates = workShift.map((ws) => `${ws.ad_year}-${ws.month}-${ws.day}`);
  const uniqueDates = Array.from(new Set(rawDates)).map((d) => moment(d, "YYYY-MM-DD"));

  if (uniqueDates.length === 0) {
    const start = moment(item.start_date);
    const end = item.end_date ? moment(item.end_date) : start;

    const days: Moment[] = [];
    for (let d = moment(start); d.isSameOrBefore(end); d.add(1, "day")) {
      days.push(moment(d));
    }

    return days;
  }

  return uniqueDates.sort((a, b) => a.diff(b));
};

const TableSelfServiceLeaveHistory = (props: TableSelfServiceLeaveHistoryProps) => {
  const { leaveRequestList, leaveTypeList, shiftTimeMap } = props;

  return (
    <div className="table-slh">
      <Table celled compact structured>
        <TableHeader>
          <TableRow>
            <TableHeaderCell textAlign="center">Leave Type</TableHeaderCell>
            <TableHeaderCell style={{ width: "95px" }} textAlign="center">
              Remaining Leave Days
            </TableHeaderCell>
            <TableHeaderCell textAlign="center">Leave Date</TableHeaderCell>
            <TableHeaderCell textAlign="center">Total Day</TableHeaderCell>
            <TableHeaderCell textAlign="center">Shift Time</TableHeaderCell>
            <TableHeaderCell textAlign="center" width={2}>
              Leave Time
            </TableHeaderCell>
            <TableHeaderCell style={{ width: "85px" }} textAlign="center">
              Requested hour
            </TableHeaderCell>
            <TableHeaderCell style={{ width: "115px" }} textAlign="center">
              Under/Over quota leave (hr.)
            </TableHeaderCell>
            <TableHeaderCell textAlign="center">Documents</TableHeaderCell>
            <TableHeaderCell textAlign="center">Status</TableHeaderCell>
            <TableHeaderCell style={{ minWidth: "115px" }} textAlign="center" />
          </TableRow>
        </TableHeader>
        {/* Table Data */}
        <TableBody>
          {leaveRequestList.length > 0 ? (
            formatLeaveRequestData(leaveRequestList, shiftTimeMap).map((item) =>
              item.leaveDates.map((date, index) => {
                const isFirstRow = index === 0;

                return (
                  <TableRow key={`${item.id}-${date.format("YYYY-MM-DD")}`}>
                    {isFirstRow && (
                      <>
                        <TableCell
                          style={{ verticalAlign: "top" }}
                          rowSpan={item.leaveDates.length}
                          textAlign="center"
                        >
                          <RenderLeaveType item={item} leaveTypeOptions={leaveTypeList} />
                        </TableCell>
                        <TableCell
                          style={{ verticalAlign: "top" }}
                          rowSpan={item.leaveDates.length}
                          textAlign="center"
                        >
                          {item.total_remaining_leave_days}
                        </TableCell>
                      </>
                    )}

                    <TableCell textAlign="center">{date.format("DD/MM/YYYY")}</TableCell>

                    {isFirstRow && (
                      <TableCell
                        style={{ verticalAlign: "top" }}
                        rowSpan={item.leaveDates.length}
                        textAlign="center"
                      >
                        {item.total_leave_days}
                      </TableCell>
                    )}

                    <TableCell textAlign="center" verticalAlign="top">
                      <RenderDisplayShiftTime
                        date={date}
                        workShiftList={item.work_shift_detail.work_shift ?? []}
                      />
                    </TableCell>
                    <TableCell textAlign="center" verticalAlign="top">
                      <RenderDisplayLeaveTime
                        date={date}
                        workShiftList={item.work_shift_detail?.work_shift || []}
                      />
                    </TableCell>
                    <TableCell textAlign="center" verticalAlign="top">
                      <RenderDisplayRequestedHour
                        date={date}
                        shiftTime={item.shiftTimeMapByDate[date.format("YYYY-MM-DD")]}
                        status={item.status}
                        totalLeaveDays={item.total_leave_days}
                        workShiftItem={item.work_shift_detail}
                      />
                    </TableCell>
                    <TableCell textAlign="center" verticalAlign="top">
                      <RenderDisplayQuotaLeaveHour
                        date={date}
                        shiftTime={item.shiftTimeMapByDate[date.format("YYYY-MM-DD")]}
                        status={item.status}
                        totalLeaveDays={item.total_leave_days}
                        workShiftItem={item.work_shift_detail}
                      />
                    </TableCell>

                    {isFirstRow && (
                      <>
                        <TableCell
                          style={{ verticalAlign: "top" }}
                          rowSpan={item.leaveDates.length}
                        >
                          <RenderDocument item={item} leaveTypeList={leaveTypeList} />
                        </TableCell>
                        <TableCell
                          style={{ verticalAlign: "top" }}
                          rowSpan={item.leaveDates.length}
                          textAlign="center"
                        >
                          <RenderStatus item={item} status={item.status} remark={item.reject_remark} />
                        </TableCell>
                        <TableCell
                          style={{ verticalAlign: "top" }}
                          rowSpan={item.leaveDates.length}
                          textAlign="center"
                        >
                          <RenderActionButton
                            setProp={props.setProp}
                            onOpenRequest={props.onOpenRequest}
                            onCancel={props.onCancel}
                            item={item}
                            paramKey={props.paramKey}
                          />
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })
            )
          ) : (
            <TableCell colSpan="11" style={{ color: "grey" }} textAlign="center">
              No rows found
            </TableCell>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

TableSelfServiceLeaveHistory.displayName = "TableSelfServiceLeaveHistory";

export default React.memo(TableSelfServiceLeaveHistory);
